import { ReportImg } from "@/components/report/commons/ReportImg";
import SubtitleTypography from "@/components/report/commons/SubtitleTypography";
import { usePageUserType } from "@/utils/router";
import { Box, Button, Chip, Stack, styled, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { WrappedSection } from "./commons";
import JudgementCriteriaDialogButton from "./HelpDialog/JudgementCriteriaDialogButton";

const ColorConfig: Record<
  string,
  { background: string; text: string; accentBackground: string }
> = {
  A: {
    background: "rgb(0 86 160 / 0.95)",
    text: "rgb(0 86 160 / 1)",
    accentBackground: "rgb(0 51 121 / 0.9)",
  },
  B: {
    background: "rgb(1 111 151 / 0.95)",
    text: "rgb(1 111 151 / 1)",
    accentBackground: "rgb(1 67 109 / 0.9)",
  },
  C: {
    background: "rgb(255 177 14 / 0.95)",
    text: "rgb(255 177 14 / 1)",
    accentBackground: "rgb(255 142 8 / 0.9)",
  },
  D: {
    background: "rgb(255 145 11 / 0.95)",
    text: "rgb(255 177 14 / 1)",
    accentBackground: "rgb(255 97 7 / 0.9)",
  },
  E: {
    background: "rgb(247 57 47 / 0.95)",
    text: "rgb(247 57 47 / 1)",
    accentBackground: "rgb(179 41 34 / 0.9)",
  },
  "-": {
    background: "rgb(112 112 112 / 0.95)",
    text: "rgb(112 112 112 / 1)",
    accentBackground: "rgb(62 62 62 / 0.9)",
  },
} as const;

const SpeechBalloon = styled("div")({
  width: "100%",
  position: "relative",
  display: "inline-block",
  margin: "0 0 0 28px",
  padding: "16px 16px",
  background: "#FFFFFF",
  boxSizing: "border-box",
  borderRadius: "8px 8px 8px 8px",
  "&:before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "-24px",
    marginTop: "-12px",
    border: "12px solid transparent",
    borderRight: "24px solid #FFFFFF",
    zIndex: 2,
  },
});

export interface TotalJudgeProps {
  value: string;
  summary: string;
  previous_value: string;
  previous_summary: string;
  previous_uuid: string;
  description_lines: string[];
}

export default function TotalJudge({
  value,
  summary,
  previous_value,
  previous_summary,
  previous_uuid,
  description_lines,
}: TotalJudgeProps) {
  const userType = usePageUserType();
  const colors = ColorConfig[value?.at(0) ?? "-"];
  if (colors == null) return <></>;
  return (
    <Stack sx={{ mb: 12 }}>
      <Stack
        sx={{ mt: "48px", mx: 6, mb: 8 }}
        direction="row"
        justifyContent="space-between"
      >
        <SubtitleTypography>総合判定</SubtitleTypography>
        <JudgementCriteriaDialogButton />
      </Stack>
      <Stack
        sx={{
          pt: 10,
          px: 10,
          pb: 6,
          backgroundColor: colors.background,
          backgroundImage: "url(/img/image_report_noise.png)",
          backgroundBlendMode: "lighten",
          gap: "19px",
        }}
      >
        <Stack direction="row">
          <ReportImg
            image_name={`image_report_face_${value[0].toLowerCase()}.svg`}
            style={{
              filter: `drop-shadow(0px 3px 3px ${
                value[0] !== "-" ? "#808080" : "#4e4e4e"
              }`,
            }}
          />
          <SpeechBalloon
            style={{
              boxShadow: `0px 2px 5px 1px ${
                value[0] !== "-" ? "#808080" : "#4e4e4e"
              }`,
            }}
          >
            <Stack
              sx={{
                alignItems: "center",
                gap: 1,
                pt: "14px",
                pb: "18px",
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-end"
                spacing={1}
                height="48px"
                position="relative"
              >
                {value[0] !== "-" ? (
                  <>
                    <Typography
                      fontFamily="Montserrat"
                      fontWeight="700"
                      fontSize="48px"
                      lineHeight="32px"
                      color={colors.text}
                    >
                      {value[0]}
                    </Typography>
                    <Typography
                      fontWeight="700"
                      fontSize="14px"
                      color={colors.text}
                      lineHeight={1}
                    >
                      判定{value[1] === "*" && <sup>※</sup>}
                    </Typography>
                  </>
                ) : (
                  <Stack direction="row">
                    <Typography
                      fontWeight="700"
                      fontSize="28px"
                      color={colors.text}
                      lineHeight="175%"
                    >
                      判定不能
                    </Typography>
                    {value[1] === "*" && (
                      <Typography
                        fontWeight="700"
                        fontSize="11.6px"
                        color={colors.text}
                        lineHeight={1}
                        sx={{ pt: 2.5 }}
                      >
                        ※
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
              <Stack fontWeight="700" fontSize="16px" color="text.primary">
                {summary}
              </Stack>
            </Stack>
          </SpeechBalloon>
        </Stack>
        {previous_value != null && (
          <Stack textAlign="center">
            <Box>
              <Chip
                label={
                  <Button
                    size="small"
                    sx={{ color: "text.secondary" }}
                    component={RouterLink}
                    to={
                      userType === "examinee"
                        ? `/${userType}/report/${previous_uuid}/`
                        : ""
                    }
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "12px",
                        textAlign: "center",
                        color: "#FFFFFF",
                        textDecoration: "underline",
                      }}
                    >
                      前回判定：
                      {previous_value[0] === "-"
                        ? "判定不能"
                        : previous_value[0]}
                      {previous_value[1] === "*" && <sup>※</sup>} （
                      {previous_summary}）
                    </Typography>
                  </Button>
                }
                sx={{
                  backgroundImage: "url(/img/image_report_noise.png)",
                  backgroundColor: colors.accentBackground,
                  backgroundBlendMode: "lighten",
                  px: 4,
                }}
              />
            </Box>
          </Stack>
        )}
      </Stack>
      <Stack sx={{ mt: 6, mx: 6 }}>
        {description_lines?.map((d, i) => (
          <Typography
            key={i}
            variant="body1"
            sx={{
              color: "text.primary",
              textAlign: "justify",
              whiteSpace: "pre-wrap",
            }}
          >
            {d}
          </Typography>
        ))}
      </Stack>
      {value.at(-1) === "*" && (
        <Stack sx={{ mx: 6 }}>
          <WrappedSection title="※治療中のあなたへ" color="#FF910B">
            <Typography variant="body2" color="text.secondary">
              あなたは睡眠に関して治療中のためかかりつけの医師のもとで治療を継続しましょう。本測定は日常的に良質な睡眠がとれているかを判定しているため、あくまで参考情報となります。
            </Typography>
          </WrappedSection>
        </Stack>
      )}
    </Stack>
  );
}
